import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useFarmOrder } from '../hooks/useFarmOrder';
import { translateFarmsError } from '../lib/translateError';
import { formatBRL } from '@/lib/format';
import { PageShell } from '@/components/layout/PageShell';
import type { FarmOrder } from '../types';

export function Checkout() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const { simulatePayment, loading } = useFarmOrder();
  const navigate = useNavigate();

  const [order, setOrder]     = useState<FarmOrder | null>(null);
  const [method, setMethod]   = useState<'pix' | 'credit_card' | 'debit_card'>('pix');
  const [paid, setPaid]       = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!orderId) return;
    supabase.from('farm_orders').select('*').eq('id', orderId).single().then(({ data, error: fetchErr }) => {
      if (fetchErr || !data) { setError('Pedido não encontrado.'); return; }
      setOrder(data as unknown as FarmOrder);
    });
  }, [orderId]);

  async function handlePay() {
    try {
      await simulatePayment(orderId, method);
      setPaid(true);
    } catch (e: any) {
      setError(e.message ?? translateFarmsError(''));
    }
  }

  if (!order) return <PageShell title="Carregando…" back="/farms/pedidos"><div className="h-40 bg-surface-subtle rounded-2xl animate-pulse" /></PageShell>;

  if (paid) return (
    <PageShell title="Pagamento confirmado" back="/farms/pedidos">
      <div className="card p-10 flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-positive/10 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-positive" />
        </div>
        <h2 className="font-display font-bold text-2xl text-ink">Pedido reservado!</h2>
        <p className="text-sm text-ink-muted text-center max-w-sm">
          Seu pedido foi registrado. A farm irá confirmar a disponibilidade em breve.
        </p>
        <p className="text-xs text-ink-soft border border-border rounded-xl px-4 py-2">
          💳 Integração Mercado Pago Marketplace chega na v2.
        </p>
        <button onClick={() => navigate('/farms/pedidos')} className="btn-primary mt-2">Ver meus pedidos</button>
      </div>
    </PageShell>
  );

  return (
    <PageShell title="Checkout" back={`/farms/pedidos/${orderId}`} maxWidth="sm">
      <div className="space-y-4">
        <div className="card p-5">
          <h2 className="font-semibold text-ink mb-3">Resumo do pedido</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-ink-muted">Subtotal fornecedor</span><span>{formatBRL(order.supplier_amount_cents)}</span></div>
            <div className="flex justify-between"><span className="text-ink-muted">Taxa plataforma (7%)</span><span>{formatBRL(order.platform_fee_cents)}</span></div>
            <div className="flex justify-between font-bold border-t border-border pt-2 text-base"><span>Total</span><span className="text-brand-700">{formatBRL(order.total_amount_cents)}</span></div>
          </div>
        </div>

        <div className="card p-5">
          <h2 className="font-semibold text-ink mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" />Forma de pagamento</h2>
          <div className="space-y-2">
            {([['pix','PIX'],['credit_card','Cartão de crédito'],['debit_card','Cartão de débito']] as const).map(([val, label]) => (
              <label key={val} className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:bg-surface-subtle">
                <input type="radio" name="method" checked={method===val} onChange={()=>setMethod(val)} />
                <span className="text-sm font-medium text-ink">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-negative">{error}</p>}

        <button onClick={handlePay} disabled={loading} className="btn-primary w-full gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Pagar {formatBRL(order.total_amount_cents)}
        </button>

        <p className="text-xs text-ink-soft text-center">
          Pagamento simulado — integração MP Marketplace na v2.
        </p>
      </div>
    </PageShell>
  );
}
